import React, { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Text, Box, Flex, Image, Portal, FormErrorMessage } from '@chakra-ui/core'
import { CircleUser } from 'lucide-react'

import { StyledFormControl, StyledFormLabel } from 'components/AdminConsole/commonUI/styles'
import { InputWithIcon } from 'components/AdminConsole/commonUI/InputWithIcon'
import { FileUploadButton } from 'components/TasksV2/TaskCreation/Steps/SubtaskCreationStep/Widgets/Link/FIleUploadButton'
import { DropzoneWrapper } from 'components/DropzoneWrapper'
import useLocaleText from 'components/useLocaleText'
import { useDropzoneHandlers } from 'hooks/useDropzoneHandlers'
import { useMediaState } from 'hooks/useMediaState'
import { isRtlSelector } from 'redux/selectors/general'
import strings from 'constants/strings'
import HiddenFileInput from 'components/ui/Input/HiddenFileInput'
import { RemoveButton } from 'components/ui/Button/RemoveButton'
import FsLightbox from 'fslightbox-react'
import { PROFILE_IMAGE_PLACEHOLDER, validateImageUrl } from '../utils'
import { ControlledSwitch } from 'components/ui/ControlledSwitch'
import { RootState } from 'constants/interfaces'

interface ProfileImageFieldProps {
  defaultImageUrl?: string
  isCustomAvatarOption?: boolean
  isCustomAvatar?: boolean
  isEdit?: boolean
  setIsCustomAvatar?: (isCustomAvatar: boolean) => void
}

export const ProfileImageField = ({
  isCustomAvatarOption,
  isCustomAvatar,
  setIsCustomAvatar,
  isEdit = false,
  defaultImageUrl = PROFILE_IMAGE_PLACEHOLDER,
}: ProfileImageFieldProps) => {
  // Hooks
  const { register, setValue, triggerValidation, watch, errors, getValues } = useFormContext()
  const { retailUsersObject } = useSelector((state: RootState) => state.config.config)
  const isRtl = useSelector(isRtlSelector)
  const t = useLocaleText('profile_image', true)
  const t_placeholder = useLocaleText('pop_image_placeholder', true)
  const set_custom_avatar = useLocaleText('set_custom_avatar', true)

  const [isOpenLightBox, setIsOpenLightBox] = useState(false)

  const {
    handleSingleMediaUploading,
    imageInputRef,
    setUploadedImagesURL,
    uploadedImagesURL,
    handleDeleteMedia,
    media,
    isUploadLoading,
  } = useMediaState({
    images: !isCustomAvatarOption ? [getValues('data.profile_image_url') || defaultImageUrl] : [],
  })

  const initilalProfileImageUrl = useMemo(() => {
    return watch('data.profile_image_url')
  }, [watch])

  const selectedUser = isCustomAvatarOption ? retailUsersObject?.[watch('users')?.[0]] : null

  const profileImageUrl = selectedUser?.avatar_profile_img_url || selectedUser?.profile_img_url

  const error = errors.data?.profile_image_url

  // Event handlers
  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const data = await handleSingleMediaUploading(event)
    if (!data?.uploadedFileUrl) {
      return
    }
    const newImage = data.uploadedFileUrl
    setValue('data.profile_image_url', newImage)
    triggerValidation?.('data.profile_image_url')
  }

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value
    setValue('data.profile_image_url', url)

    const validationResult = validateImageUrl(url)
    if (validationResult === true && url) {
      setUploadedImagesURL([url])
    } else if (!url) {
      setUploadedImagesURL([])
    }

    triggerValidation?.('data.profile_image_url')
  }

  const handleUploadMedia = async (files: File[]) => {
    if (!files.length) return

    const mockEvent = {
      target: {
        files: files as unknown as FileList,
      },
    } as React.ChangeEvent<HTMLInputElement>

    await handleImageUpload(mockEvent)
  }

  const openViewer = () => {
    setIsOpenLightBox((prev) => !prev)
  }

  const handleRemoveMedia = () => {
    handleDeleteMedia(0)
    setValue('data.profile_image_url', '')
    triggerValidation?.('data.profile_image_url')
  }

  const handleCustomAvatarToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsCustomAvatar?.(e.target.checked)
    if (e.target.checked) {
      setUploadedImagesURL([defaultImageUrl])
      setValue('data.profile_image_url', defaultImageUrl, true)
    } else {
      setValue('data.profile_image_url', profileImageUrl, true)
      setUploadedImagesURL([profileImageUrl || ''])
    }
    triggerValidation?.('data.profile_image_url')
  }

  const handleUploadMediaClick = () => {
    imageInputRef.current?.click()
  }

  // Dropzone handlers
  const { onDrop, handlePaste } = useDropzoneHandlers({
    onUploadMedia: handleUploadMedia,
    accept: `${strings.POST_FILE_ACCEPT_EXTENSIONS},image/*`,
  })

  useEffect(() => {
    if (!isCustomAvatarOption) {
      const profileImageUrl = getValues('data.profile_image_url') || defaultImageUrl
      setValue('data.profile_image_url', profileImageUrl)
      setUploadedImagesURL([profileImageUrl])
    }
  }, [defaultImageUrl, getValues, isCustomAvatarOption, setUploadedImagesURL, setValue])

  useEffect(() => {
    if (isCustomAvatarOption && profileImageUrl && !isCustomAvatar) {
      if (initilalProfileImageUrl) {
        setUploadedImagesURL([initilalProfileImageUrl])
      } else {
        setUploadedImagesURL([profileImageUrl])
      }
    }
  }, [initilalProfileImageUrl, isCustomAvatar, isCustomAvatarOption, profileImageUrl, setUploadedImagesURL])

  return (
    <DropzoneWrapper onDrop={onDrop} onPaste={handlePaste}>
      <StyledFormControl w="100%" isInvalid={!!error}>
        <Flex alignItems="flex-end" style={{ columnGap: '10px' }}>
          <Box position="relative" width="50px" height="50px" borderRadius="10px" overflow="hidden" cursor="pointer">
            {uploadedImagesURL[0] ? (
              <Image
                src={uploadedImagesURL[0]}
                width="100%"
                height="100%"
                alt={'Uploaded'}
                objectFit="cover"
                onClick={openViewer}
              />
            ) : (
              <Flex width="100%" height="100%" bg="gray.200" alignItems="center" justifyContent="center">
                <CircleUser size={24} color="#A0AEC0" strokeWidth={1.5} />
              </Flex>
            )}
            {uploadedImagesURL[0] && (!isCustomAvatarOption || (isCustomAvatarOption && isCustomAvatar)) && (
              <RemoveButton
                aria-label="Remove File"
                onClick={handleRemoveMedia}
                pos="absolute"
                minW="unset"
                w="16px"
                h="16px"
                right={isRtl ? 'unset' : '4px'}
                left={isRtl ? '4px' : 'unset'}
              />
            )}
          </Box>
          <Flex
            flexDirection="column"
            style={{ columnGap: '10px' }}
            w="100%"
            pointerEvents={isUploadLoading ? 'none' : 'auto'}
            opacity={isUploadLoading ? 0.5 : 1}
          >
            <StyledFormLabel style={{ columnGap: '10px' }} justifyContent="space-between">
              <Flex alignItems="center" style={{ columnGap: '10px' }}>
                <CircleUser size={18} strokeWidth={2.5} />
                <Text>{t}</Text>
              </Flex>
              {isCustomAvatarOption && (
                <Flex alignItems="center" style={{ columnGap: '10px' }} display={isEdit ? 'none' : 'flex'}>
                  <Text fontSize="14px" fontWeight="bold" color="gray.500">
                    {set_custom_avatar}
                  </Text>
                  <ControlledSwitch isChecked={isCustomAvatar} onChange={handleCustomAvatarToggle} />
                </Flex>
              )}
            </StyledFormLabel>

            <Box
              pos="relative"
              pointerEvents={!isCustomAvatar && isCustomAvatarOption ? 'none' : 'auto'}
              opacity={!isCustomAvatar && isCustomAvatarOption ? 0.5 : 1}
              cursor={!isCustomAvatar && isCustomAvatarOption ? 'not-allowed' : 'pointer'}
            >
              <InputWithIcon
                name="data.profile_image_url"
                pr={isRtl ? '1rem' : '2.5rem'}
                pl={isRtl ? '2.5rem' : '1rem'}
                borderRadius="10px"
                ref={register({
                  validate: validateImageUrl,
                })}
                onChange={handleUrlChange}
                border="1px solid"
                placeholder={t_placeholder}
                _focus={{ boxShadow: '0px 0px 0px 1px black' }}
              />
              <Box
                pos="absolute"
                right={isRtl ? 'unset' : '10px'}
                left={isRtl ? '10px' : 'unset'}
                top="50%"
                transform="translateY(-50%)"
              >
                <FileUploadButton onClick={handleUploadMediaClick} />
              </Box>
            </Box>
          </Flex>
        </Flex>

        <FormErrorMessage>{error?.message}</FormErrorMessage>
      </StyledFormControl>

      <HiddenFileInput accept="image/*" onChange={handleImageUpload} ref={imageInputRef} />

      <Portal container={document.getElementById('root')}>
        <FsLightbox
          key={JSON.stringify(media)}
          toggler={isOpenLightBox}
          sources={media}
          sourceIndex={0}
          types={['image']}
        />
      </Portal>
    </DropzoneWrapper>
  )
}
